import { useState } from "react";
import { useShedConfig } from "../../contexts/ShedConfigContext";
import InfoTooltip from "../layout/InfoTooltip";
import QuantitySelector from "./QuantitySelector";

const doorTypes = [
  {
    id: "rollUp" as const,
    name: "Roll up door",
    tooltip: "Garage-type rolling door, ideal for wide access.",
    image: "/images/doors/roll-up-front.png",
    hasSize: true,
  },
  {
    id: "single" as const,
    name: "Single wooden door",
    tooltip: "Standard wooden door, aesthetic and functional for storage use.",
    image: "/images/doors/single-front.png",
  },
  {
    id: "double" as const,
    name: "Double wooden door",
    tooltip:
      "Double door, ideal for wide entrances. Facilitates access for large equipment or better ventilation.",
    image: "/images/doors/double-front.png",
  },
  {
    id: "house" as const,
    name: "House door",
    tooltip:
      "House-type door, ideal if you want a more secure or formal entrance, purely residential.",
    image: "/images/doors/house-front.png",
  },
];

export default function DoorsAccordion() {
  const { doors, toggleDoor, setDoorQuantity, setDoorSize } = useShedConfig();
  const [isMainOpen, setIsMainOpen] = useState(false);
  const [openDoors, setOpenDoors] = useState<Record<string, boolean>>({});

  const toggleMain = () => setIsMainOpen(!isMainOpen);
  const toggleDoorSection = (id: string) => {
    setOpenDoors((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="gap-3 w-full">
      <button
        type="button"
        onClick={toggleMain}
        className="flex items-center justify-between w-full p-2 font-medium text-left text-gray-900 border border-gray-200 rounded-xl hover:bg-gray-100 hover:cursor-pointer"
      >
        <span className="ml-4 text-xl">
          Doors
          <InfoTooltip>
            Select the type of door your structure will have.
          </InfoTooltip>
        </span>
        <svg
          className={`w-3 h-3 shrink-0 transition-transform ${isMainOpen ? "" : "rotate-180"}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 5 5 1 1 5"
          />
        </svg>
      </button>

      {isMainOpen && (
        <div className="border border-gray-200 border-t-0 rounded-b-xl">
          {doorTypes.map((doorType) => (
            <div
              key={doorType.id}
              className="border-b border-gray-200 last:border-b-0"
            >
              <button
                type="button"
                onClick={() => toggleDoorSection(doorType.id)}
                className="flex items-center justify-between w-full p-3 font-medium text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer"
              >
                <span className="ml-4">{doorType.name}</span>
                <svg
                  className={`w-3 h-3 shrink-0 transition-transform ${openDoors[doorType.id] ? "" : "rotate-180"}`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 10 6"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5 5 1 1 5"
                  />
                </svg>
              </button>

              {openDoors[doorType.id] && (
                <div className="p-4 bg-[#fcf8efe1]">
                  <div className="text-center">
                    <input
                      type="checkbox"
                      checked={doors[doorType.id].selected}
                      onChange={() => toggleDoor(doorType.id)}
                      className="mr-2"
                    />
                    {doorType.name}
                    <InfoTooltip>{doorType.tooltip}</InfoTooltip>

                    <img
                      src={doorType.image}
                      alt={doorType.name}
                      className="w-56 items-center mx-auto my-2"
                    />

                    {doorType.hasSize && (
                      <input
                        type="text"
                        value={doors[doorType.id].size || ""}
                        onChange={(e) =>
                          setDoorSize(doorType.id, e.target.value)
                        }
                        className="w-1/2 items-center p-2 border border-gray-300 rounded mb-2"
                        placeholder="Add door size"
                      />
                    )}

                    <div className="flex justify-center mb-2">
                      <QuantitySelector
                        value={doors[doorType.id].quantity}
                        onChange={(qty) => setDoorQuantity(doorType.id, qty)}
                        disabled={!doors[doorType.id].selected}
                      />
                    </div>

                    <p className="text-[8px] text-gray-500 ml-2">
                      Use the (+/-) buttons to select how many doors you want to
                      install of that type.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
